import { useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { track } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "min-h-[52px] w-full rounded-xl border border-input bg-card px-4 py-3 text-lg text-foreground placeholder:text-muted-foreground/70 transition-colors duration-200 focus:border-primary";

// The single inquiry form used site-wide (contact page + schedule pop-up).
// Sends name, number and optional comments to Dorothy's inbox.
export default function MessageForm() {
  const [form, setForm] = useState({ name: "", number: "", comments: "", company: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [failed, setFailed] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function onSubmit(e) {
    e.preventDefault();
    setSending(true);
    setFailed("");
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = typeof data.detail === "string" ? data.detail : "Your message could not be sent just now.";
        throw new Error(detail);
      }
      track("contact_form_submitted");
      setSent(true);
    } catch (err) {
      setFailed(err.message);
      toast.error("Message not sent", { description: "Please try again in a few minutes." });
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <div className="rounded-2xl border border-border bg-secondary/50 p-8" data-testid="contact-success">
        <h2 className="font-serif text-3xl font-semibold text-primary">Thank you</h2>
        <p className="mt-4 text-lg leading-relaxed">
          Your message has been sent. Dorothy will get back to you in her own time.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
      <div>
        <label htmlFor="contact-name" className="mb-2 block text-base font-semibold">Your name</label>
        <input id="contact-name" data-testid="contact-name" required maxLength={100} value={form.name} onChange={set("name")} className={inputClass} autoComplete="name" />
      </div>

      <div>
        <label htmlFor="contact-number" className="mb-2 block text-base font-semibold">Your number</label>
        <input id="contact-number" data-testid="contact-number" type="tel" required maxLength={20} value={form.number} onChange={set("number")} className={inputClass} autoComplete="tel" placeholder="So Dorothy can reach you back" />
      </div>

      <div>
        <label htmlFor="contact-comments" className="mb-2 block text-base font-semibold">Comments or requests <span className="font-normal text-muted-foreground">(optional)</span></label>
        <textarea id="contact-comments" data-testid="contact-comments" rows={4} maxLength={1000} value={form.comments} onChange={set("comments")} className={`${inputClass} min-h-[120px] resize-y`} placeholder="Anything you would like Dorothy to know, or times that suit you" />
      </div>

      {/* Honeypot: hidden from people, tempting to bots */}
      <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
        <label htmlFor="contact-company">Company</label>
        <input id="contact-company" tabIndex={-1} autoComplete="off" value={form.company} onChange={set("company")} />
      </div>

      {failed && (
        <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-base" role="alert" data-testid="contact-error">
          <p className="font-semibold">{failed}</p>
          <p className="mt-1">Please try again in a few minutes.</p>
        </div>
      )}

      <button type="submit" data-testid="contact-submit" disabled={sending} className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-60">
        <Send size={18} aria-hidden /> {sending ? "Sending..." : "Send message"}
      </button>
    </form>
  );
}
